import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Grid,
  Image,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
  IconButton,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, CloseIcon } from '@chakra-ui/icons';
import AddWorkModal from './AddWorkModal';
import AddBrandModal from './AddBrandModal';
import PortfolioContentViewModal from './PortfolioContentViewModal';
import AddRateModal from './AddRateModal';
import EditRateModal from './EditRateModal';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { brandApi } from '../../../services/api/brand';
import { contentPortfolioApi, ContentPortfolioDto } from '../../../services/api/contentPortfolio';
import { influencerApi } from '../../../services/api/influencer';
import { UgcRateRange } from '@foodfluence/shared';
import { UGC_RATE_PRESETS, UgcRatePreset, getUgcRateDisplayName } from '../../../utils/contentUtils';
import { ff } from '../../../theme';

interface UgcRate extends UgcRateRange {
  id: string;
}

interface UgcPortfolioProps {
  onBack: () => void;
}

const UgcPortfolio: React.FC<UgcPortfolioProps> = ({ onBack }) => {
  const { influencerData } = useContext(InfluencerDataContext);
  const [isAddWorkModalOpen, setIsAddWorkModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isAddRateModalOpen, setIsAddRateModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<UgcRate | null>(null);
  const [ugcRates, setUgcRates] = useState<UgcRate[]>(() =>
    Array.isArray(influencerData?.ugcRateRanges)
      ? influencerData.ugcRateRanges.map((r, i) => ({ ...r, id: `rate-${i}` }))
      : []
  );
  const [brandIcons, setBrandIcons] = useState<{ [brandName: string]: string }>({});
  const [isLoadingBrandIcons, setIsLoadingBrandIcons] = useState(false);
  const [portfolioContent, setPortfolioContent] = useState<ContentPortfolioDto[]>([]);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(false);
  const [selectedContent, setSelectedContent] = useState<ContentPortfolioDto | null>(null);
  const toast = useToast();

  // Get brands from influencer data
  const brands = influencerData.brands || [];

  // Fetch brand icons when component mounts or when brands change
  useEffect(() => {
    const fetchBrandIcons = async () => {
      if (!brands || brands.length === 0) {
        setBrandIcons({});
        return;
      }

      setIsLoadingBrandIcons(true);
      try {
        const icons = await brandApi.getBrandIcons(brands);
        const iconMap: { [key: string]: string } = {};
        icons.forEach(brand => {
          iconMap[brand.name] = brand.icon;
        });
        setBrandIcons(iconMap);
      } catch (error) {
        console.error('Error fetching brand icons:', error);
      } finally {
        setIsLoadingBrandIcons(false);
      }
    };

    fetchBrandIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands.length]);

  // Fetch portfolio content
  const fetchPortfolioContent = async () => {
    setIsLoadingPortfolio(true);
    try {
      const content = await contentPortfolioApi.getPortfolioContent(influencerData.id);
      setPortfolioContent(content);
    } catch (error) {
      console.error('Error fetching portfolio content:', error);
      toast({
        title: 'Error loading portfolio',
        description: 'Failed to load your portfolio content',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingPortfolio(false);
    }
  };

  useEffect(() => {
    fetchPortfolioContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteBrand = async (brandName: string) => {
    try {
      await brandApi.removeBrandFromInfluencer(brandName, influencerData.id);
      toast({
        title: "Brand removed",
        description: `${brandName} has been removed from your profile`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      // Context will automatically update via Firestore listener
    } catch (error) {
      console.error('Error removing brand:', error);
      toast({
        title: "Error removing brand",
        description: "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      await contentPortfolioApi.deletePortfolioContent(contentId, influencerData.id);
      
      // Remove from local state
      setPortfolioContent(prev => prev.filter(item => item.contentId !== contentId));
      
      toast({
        title: "Content deleted",
        description: "Content has been removed from your portfolio",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: "Error deleting content",
        description: "Please try again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const dismissUgcBanner = () => {
    if (influencerData?.showCreatePortfolioBanner && influencerData.id) {
      influencerApi.updateProfile({ showCreatePortfolioBanner: false }, influencerData.id).catch(err =>
        console.error('Failed to dismiss UGC banner:', err)
      );
    }
  };

  const handleContentAdded = () => {
    fetchPortfolioContent();
    dismissUgcBanner();
  };

  const availableRateOptions = UGC_RATE_PRESETS.filter(
    (preset) => !ugcRates.some(
      (r) => getUgcRateDisplayName(r) === preset.displayName
    )
  );

  const syncRatesToBackend = async (rates: UgcRate[]) => {
    try {
      const ugcRateRanges = rates.map(({ id: _id, ...rest }) => rest);
      await influencerApi.updateProfile({ ugcRateRanges }, influencerData.id);
    } catch (error) {
      console.error('Failed to sync UGC rates:', error);
      toast({
        title: 'Failed to save rates',
        description: 'Your changes may not have been saved. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddRate = (preset: UgcRatePreset, price: number) => {
    const { displayName: _, ...rateFields } = preset;
    const newRates = [
      ...ugcRates,
      { ...rateFields, id: `rate-${Date.now()}-${Math.random().toString(36).slice(2)}`, minRate: price },
    ];
    setUgcRates(newRates);
    setIsAddRateModalOpen(false);
    syncRatesToBackend(newRates);
    dismissUgcBanner();
    toast({
      title: 'Rate added',
      description: `${getUgcRateDisplayName(preset)} has been added to your rates`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleUpdateRate = (id: string, price: number) => {
    const newRates = ugcRates.map((r) => (r.id === id ? { ...r, minRate: price } : r));
    setUgcRates(newRates);
    setEditingRate(null);
    syncRatesToBackend(newRates);
    toast({
      title: 'Rate updated',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDeleteRate = (id: string) => {
    const newRates = ugcRates.filter((r) => r.id !== id);
    setUgcRates(newRates);
    syncRatesToBackend(newRates);
    toast({
      title: 'Rate removed',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box bg="white" minH="100vh">
      <Box maxW="container.md" mx="auto">
      <VStack spacing={8} align="stretch">
        {/* Previous Work Section */}
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text as="h3" m={0} textStyle="display" fontSize="20px" color={ff.ink}>
              Previous work
            </Text>
            <Button
              variant="inkOutline"
              h="44px"
              px={4}
              fontSize="14px"
              onClick={() => setIsAddWorkModalOpen(true)}
              leftIcon={<AddIcon boxSize={3} />}
              isDisabled={portfolioContent.length >= 4}
            >
              Add
            </Button>
          </Flex>

          {isLoadingPortfolio ? (
            <Flex justify="center" py={8}>
              <Spinner size="lg" color={ff.ink} />
            </Flex>
          ) : portfolioContent.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={12}
              px={4}
              bg={ff.grey}
              borderRadius="20px"
              border="1.5px dashed"
              borderColor={ff.border}
            >
              <Text color={ff.text2} fontSize="14px" mb={4}>
                No content in your portfolio yet
              </Text>
              <Button
                variant="ink"
                h="44px"
                px={5}
                onClick={() => setIsAddWorkModalOpen(true)}
                leftIcon={<AddIcon boxSize={3} />}
              >
                Add your first content
              </Button>
            </Flex>
          ) : (
            <Grid templateColumns={{ base: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }} gap={3}>
              {portfolioContent.map((work) => (
                <Box 
                  key={work.contentId} 
                  position="relative"
                  cursor="pointer"
                  onClick={() => setSelectedContent(work)}
                  transition="transform 0.2s"
                  _hover={{ transform: 'scale(1.02)' }}
                >
                  <Box
                    position="relative"
                    paddingBottom="75%"
                    borderRadius="20px"
                    overflow="hidden"
                    bg={ff.grey}
                  >
                    {work.contentType === 'video' ? (
                      <video
                        src={work.downloadUrl}
                        preload="metadata"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Image
                        src={work.downloadUrl}
                        alt={work.brand}
                        position="absolute"
                        top={0}
                        left={0}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                    )}
                    {work.contentType === 'video' && (
                      <Flex
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        align="center"
                        justify="center"
                      >
                        <Box
                          w={{ base: "36px", sm: "48px" }}
                          h={{ base: "36px", sm: "48px" }}
                          bg={ff.lime}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            w={0}
                            h={0}
                            borderTop="6px solid transparent"
                            borderLeft="10px solid"
                            borderLeftColor={ff.ink}
                            borderBottom="6px solid transparent"
                            ml="2px"
                          />
                        </Box>
                      </Flex>
                    )}
                    
                    {/* Delete button */}
                    <IconButton
                      aria-label="Delete content"
                      icon={
                        <Flex w="28px" h="28px" bg="white" borderRadius="full" align="center" justify="center">
                          <CloseIcon boxSize={2.5} />
                        </Flex>
                      }
                      w="44px"
                      h="44px"
                      minW="44px"
                      position="absolute"
                      top={0}
                      right={0}
                      variant="unstyled"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color={ff.ink}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening the view modal
                        handleDeleteContent(work.contentId);
                      }}
                    />
                  </Box>
                  <Text mt={2} fontSize="14px" color={ff.ink} fontWeight={600}>
                    {work.brand}
                  </Text>
                </Box>
              ))}
            </Grid>
          )}
        </Box>

        {/* Brands Worked With Section */}
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text as="h3" m={0} textStyle="display" fontSize="20px" color={ff.ink}>
              Brands worked with
            </Text>
            <Button
              variant="inkOutline"
              h="44px"
              px={4}
              fontSize="14px"
              onClick={() => setIsAddBrandModalOpen(true)}
              leftIcon={<AddIcon boxSize={3} />}
            >
              Add
            </Button>
          </Flex>

          <Flex flexWrap="wrap" gap={2}>
            {isLoadingBrandIcons && brands.length > 0 && (
              <Flex align="center" gap={2}>
                <Spinner size="sm" color={ff.muted} />
                <Text fontSize="14px" color={ff.text2}>Loading brand icons...</Text>
              </Flex>
            )}
            {!isLoadingBrandIcons && brands.length === 0 && (
              <Text fontSize="14px" color={ff.text2}>No brands added yet</Text>
            )}
            {brands.map((brandName, index) => (
              <Tag
                key={index}
                size="lg"
                borderRadius="full"
                bg={ff.grey}
                color={ff.ink}
                minH="44px"
                py={2}
              >
                {brandIcons[brandName] && (
                  <Box
                    w="24px"
                    h="24px"
                    borderRadius="xl"
                    bg="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr={2}
                    overflow="hidden"
                    flexShrink={0}
                  >
                    <Image
                      src={brandIcons[brandName]}
                      alt={brandName}
                      boxSize="20px"
                      objectFit="contain"
                      borderRadius="2xl"
                    />
                  </Box>
                )}
                <TagLabel fontWeight={600} fontSize="14px">{brandName}</TagLabel>
                <TagCloseButton
                  onClick={() => handleDeleteBrand(brandName)}
                />
              </Tag>
            ))}
          </Flex>
        </Box>

        {/* UGC Rates Section */}
        <Box>
          <Flex justify="space-between" align="center" mb={4}>
            <Text as="h3" m={0} textStyle="display" fontSize="20px" color={ff.ink}>
              UGC rates
            </Text>
            <Button
              variant="inkOutline"
              h="44px"
              px={4}
              fontSize="14px"
              onClick={() => setIsAddRateModalOpen(true)}
              leftIcon={<AddIcon boxSize={3} />}
              isDisabled={availableRateOptions.length === 0}
            >
              Add
            </Button>
          </Flex>

          {ugcRates.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={8}
              px={4}
              bg={ff.grey}
              borderRadius="20px"
              border="1.5px dashed"
              borderColor={ff.border}
            >
              <Text color={ff.text2} fontSize="14px" mb={4}>
                No rates added yet
              </Text>
              <Button
                variant="ink"
                h="44px"
                px={5}
                leftIcon={<AddIcon boxSize={3} />}
                onClick={() => setIsAddRateModalOpen(true)}
              >
                Add your first rate
              </Button>
            </Flex>
          ) : (
            <VStack spacing={3} align="stretch">
              {ugcRates.map((rate) => (
                <Flex
                  key={rate.id}
                  align="center"
                  gap={{ base: 1, sm: 3 }}
                  px={{ base: 3, sm: 4 }}
                  py={2}
                  minH="64px"
                  bg="white"
                  border="1px solid"
                  borderColor={ff.line}
                  borderRadius="20px"
                >
                  <Flex
                    w={{ base: "32px", sm: "40px" }}
                    h={{ base: "32px", sm: "40px" }}
                    bg={ff.lime}
                    borderRadius="full"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Text color={ff.ink} fontSize={{ base: "md", sm: "lg" }} fontWeight={600}>
                      $
                    </Text>
                  </Flex>

                  <Text
                    flex={1}
                    minW={0}
                    fontSize={{ base: "13px", sm: "14px" }}
                    fontWeight={600}
                    color={ff.ink}
                    noOfLines={1}
                    title={getUgcRateDisplayName(rate)}
                  >
                    {getUgcRateDisplayName(rate)}
                  </Text>

                  <Text textStyle="display" fontSize={{ base: "18px", sm: "20px" }} color={ff.ink} flexShrink={0}>
                    ${rate.minRate}
                  </Text>

                  <IconButton
                    aria-label="Edit rate"
                    icon={<EditIcon />}
                    w="44px"
                    h="44px"
                    minW="44px"
                    borderRadius="full"
                    variant="ghost"
                    color={ff.ink}
                    onClick={() => setEditingRate(rate)}
                  />

                  <IconButton
                    aria-label="Delete rate"
                    icon={<DeleteIcon />}
                    w="44px"
                    h="44px"
                    minW="44px"
                    borderRadius="full"
                    variant="ghost"
                    color={ff.danger}
                    onClick={() => handleDeleteRate(rate.id)}
                  />
                </Flex>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
      </Box>

      {/* Add Work Modal */}
      <AddWorkModal
        isOpen={isAddWorkModalOpen}
        onClose={() => setIsAddWorkModalOpen(false)}
        onSuccess={handleContentAdded}
      />

      {/* Add Brand Modal */}
      <AddBrandModal
        isOpen={isAddBrandModalOpen}
        onClose={() => setIsAddBrandModalOpen(false)}
        onSuccess={dismissUgcBanner}
      />

      {/* Content View Modal */}
      <PortfolioContentViewModal
        isOpen={selectedContent !== null}
        onClose={() => setSelectedContent(null)}
        content={selectedContent}
      />

      <AddRateModal
        isOpen={isAddRateModalOpen}
        onClose={() => setIsAddRateModalOpen(false)}
        availableOptions={availableRateOptions}
        onAdd={handleAddRate}
      />

      <EditRateModal
        isOpen={editingRate !== null}
        onClose={() => setEditingRate(null)}
        rate={editingRate}
        onSave={handleUpdateRate}
      />
    </Box>
  );
};

export default UgcPortfolio;